import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import * as serial from './serial';
import { getScannerSettings, subscribeScannerSettings } from './scannerSettings';
import { listenForWedge } from './wedge';

/**
 * One channel for everything that reads a code.
 *
 * A camera, a gun typing on the keyboard and a gun on a COM port all produce the
 * same thing — a string — and the question they all raise is the same one: who
 * is it for? Without an answer a scan either always goes to the same screen or
 * goes wherever the focus happens to be, and neither is right when someone is
 * standing at a receiving form with six lines on it.
 *
 * So: a stack. A field that wants scans registers a handler while it is the one
 * being filled in, and the topmost registered handler wins. When nobody has
 * registered, the reading falls through to the application's default — resolve
 * it and go to the record it names.
 */

type Handler = (value: string) => void;

interface ScanChannel {
  register: (handler: Handler) => () => void;
}

/** What the COM port is doing, so the settings screen can say so instead of
 *  leaving "switched on and nothing happens" to be guessed at. */
export type PortState = 'idle' | 'open' | 'no-port' | string;

let portState: PortState = 'idle';
const portListeners = new Set<() => void>();

function setPortState(next: PortState): void {
  portState = next;
  portListeners.forEach((notify) => notify());
}

export function subscribePortState(listener: () => void): () => void {
  portListeners.add(listener);
  return () => portListeners.delete(listener);
}

export function getPortState(): PortState {
  return portState;
}

/**
 * Open the port again.
 *
 * Needed because granting a port changes nothing the provider watches: the
 * person had already chosen "COM port" before pressing the button, so the mode
 * and the speed are exactly what they were, the effect does not run again, and
 * the port that was just granted is never opened. The screen then says both
 * "port chosen" and "no port chosen", which is precisely what was happening.
 */
let generation = 0;
const generationListeners = new Set<() => void>();

export function reconnectSerial(): void {
  generation += 1;
  generationListeners.forEach((notify) => notify());
}

function subscribeGeneration(listener: () => void): () => void {
  generationListeners.add(listener);
  return () => generationListeners.delete(listener);
}

function getGeneration(): number {
  return generation;
}

const ScanContext = createContext<ScanChannel | null>(null);

export function ScanProvider({
  children,
  onFallback,
}: {
  children: ReactNode;
  /** Where a reading goes when no field has claimed it. */
  onFallback: (value: string) => void;
}) {
  const handlers = useRef<Handler[]>([]);
  const fallback = useRef(onFallback);
  fallback.current = onFallback;

  const deliver = useCallback((value: string) => {
    const top = handlers.current[handlers.current.length - 1];
    if (top) top(value);
    else fallback.current(value);
  }, []);

  const register = useCallback((handler: Handler) => {
    handlers.current.push(handler);
    return () => {
      handlers.current = handlers.current.filter((entry) => entry !== handler);
    };
  }, []);

  const settings = useSyncExternalStore(subscribeScannerSettings, getScannerSettings);
  const attempt = useSyncExternalStore(subscribeGeneration, getGeneration, () => 0);

  // Plugging the scanner in is the other moment a port becomes openable without
  // anything on screen changing.
  useEffect(() => {
    const serialApi = navigator.serial;
    if (!serialApi) return;
    const again = () => reconnectSerial();
    serialApi.addEventListener('connect', again);
    return () => serialApi.removeEventListener('connect', again);
  }, []);

  // Keyboard mode costs nothing when no scanner is attached — nothing types
  // fast enough to be mistaken for one — so it stays on unless switched off.
  useEffect(() => {
    if (settings.mode === 'off') return;
    return listenForWedge(deliver);
  }, [settings.mode, deliver]);

  useEffect(() => {
    if (settings.mode !== 'serial') return;
    let session: serial.SerialSession | null = null;
    let cancelled = false;

    void serial
      .open(deliver, { baudRate: settings.baudRate })
      .then((opened) => {
        if (cancelled) void opened?.stop();
        else {
          session = opened;
          // Null means no port has been granted yet. Silence here is what made
          // "I switched it on and nothing happened" impossible to diagnose.
          setPortState(opened ? 'open' : 'no-port');
        }
      })
      .catch((exc: unknown) => {
        // Most often: the port is already open in another tab or in a terminal.
        setPortState(exc instanceof Error ? exc.message : 'failed');
      });

    return () => {
      cancelled = true;
      void session?.stop();
      setPortState('idle');
    };
  }, [settings.mode, settings.baudRate, deliver, attempt]);

  const channel = useMemo<ScanChannel>(() => ({ register }), [register]);

  return <ScanContext.Provider value={channel}>{children}</ScanContext.Provider>;
}

/**
 * Claim scans while `active`. The most recently mounted active claim wins, so a
 * dialog on top of a form takes them from the form underneath it.
 */
export function useScanTarget(handler: Handler, active = true): void {
  const channel = useContext(ScanContext);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!channel || !active) return;
    return channel.register((value) => latest.current(value));
  }, [channel, active]);
}

/** Whether scanning is wired up at all — false outside the provider. */
export function useScanAvailable(): boolean {
  return useContext(ScanContext) !== null;
}
